import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Dialog,
  DialogContent,
  Select,
  MenuItem,
  FormControl,
  makeStyles,
  withStyles,
  InputAdornment,
  Tooltip,
  Checkbox,
  ListItemText,
  Divider,
  CircularProgress,
} from "@material-ui/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import {  Edit, GetApp, Search, Clear } from "@material-ui/icons";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
// BCM-881: the ⓘ on the page title and on the quantity column headers.
import InfoIcon from "@material-ui/icons/Info";
import axios from "axios";
import { appUrl } from "../../../../../utils/config";
import CustomReportDialog, {
  formatReconDate,
  formatReconQty,
  cmoBatchesOf,
  cmoIdCell,
  cmoQtyCell,
  isZeroZeroRow,
  materialCodeCell,
  uomCell,
} from "./CustomReportDialog";
import { convertToLocaleDateTime } from "../functionalComponents/FormatDateTimeToLocale";

/*
  BCM-881 — tooltip texts for the reconciliation table, quoted from the FSD.

  BMS total qty. and Allocated Qty. are hard coded. CMO total qty. is held
  against the CMO in CMO Level Configurations and differs by CMO, so the text
  below only stands in until that configuration reaches the API.
*/
const RECON_TOOLTIP = {
  title:
    "Note - If CMO UoM is not same as BMS UoM, Mosymphony converts the quantities to match BMS UoM and display these values in the Inventory Reconciliation dashboard.",
  bmsTotalQty:
    "BMS total qty. column values are sum of - Unrestricted Stock, Quality Inspection Stock, Blocked Stock and Restricted Stock quantities.",
  cmoTotalQty:
    "CMO total qty. column values are the sum of all inventory status quantities reported by the CMO.",
  allocatedQty:
    "Allocated Qty. is the quantity from this batch already reserved against confirmed CMO orders.",
};

/*
  BCM-881 — the Search by criteria, and the fetchInventoryReconciliationData
  request field each one sets. Material Type and Plant ID are filters of their
  own on the row above.

  `kind` says what sits beside the criterion: a free text box, or a dropdown
  with the values listed. `toRequest` turns the value into request fields.
*/
const SEARCH_CRITERIA = [
  {
    value: "BMS Material Code",
    kind: "text",
    toRequest: (value) => ({ sponsorMaterialCode: value }),
  },
  {
    value: "BMS Batch Number",
    kind: "text",
    toRequest: (value) => ({ sponsorInventoryBatchNumber: value }),
  },
  {
    value: "CMO Batch Number",
    kind: "text",
    toRequest: (value) => ({ cmoInventoryBatchNumber: value }),
  },
  {
    value: "Batch Allocation Done",
    kind: "choice",
    options: ["Yes", "No"],
    toRequest: (value) => ({ batchAllocationDone: value === "Yes" }),
  },
  {
    value: "Difference",
    kind: "choice",
    options: ["Difference = 0", "Difference is not 0"],
    // The filterRowsWith values this page already sent before BCM-881.
    toRequest: (value) => ({
      filterRowsWith:
        value === "Difference = 0"
          ? "Difference(BMS-CMO) equals 0"
          : "Difference(BMS-CMO) not equals 0",
    }),
  },
];

const NO_SEARCH = { criterion: null, value: "" };

/*
  BCM-881 — the further CMO batches held against a BMS batch: those beyond the
  one the row already shows. multipleCMOBatchMapping includes the row's own
  cmoBatchNumber; it is left out, so "+N" counts the further batches as the
  FSD words it. The rows "+N" opens still list every batch, the row's own
  included, so each batch's own quantity can be seen.
*/
const extraCmoBatchesOf = (row) => {
  const batches = cmoBatchesOf(row);
  if (batches.length <= 1) return [];
  const shown = batches.findIndex(
    (batch) => (batch?.cmoInventoryBatchNumber || batch?.cmoBatchNumber) === row?.cmoBatchNumber
  );
  const skip = shown === -1 ? 0 : shown;
  return batches.filter((_, i) => i !== skip);
};

/*
  BCM-881 — the minimum width of each reconciliation column, so that cells do
  not squeeze and wrap word by word; the table scrolls across instead.
*/
const COL_MIN_WIDTH = {
  cmoId: 110,
  plantId: 90,
  brand: 130,
  materialType: 190,
  materialCode: 300,
  uom: 100,
  bmsBatch: 150,
  cmoBatch: 170,
  bmsExpiry: 130,
  cmoExpiry: 130,
  bmsTotal: 140,
  cmoTotal: 150,
  allocated: 140,
  difference: 200,
};

/*
  BCM-881 — the ⓘ tooltips: black with white text, 2px larger than MUI's
  default 10px.
*/
const InfoTooltip = withStyles({
  tooltip: {
    backgroundColor: "#000",
    color: "#fff",
    fontSize: "12px",
  },
  arrow: {
    color: "#000",
  },
})(Tooltip);

const InfoTip = ({ title }) => {
  if (!title) return null;
  return (
    <InfoTooltip title={title} arrow placement="top">
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        <InfoIcon
          style={{
            fontSize: "1rem",
            color: "#9e9e9e",
            verticalAlign: "middle",
            marginLeft: "4px",
            cursor: "pointer",
          }}
        />
      </span>
    </InfoTooltip>
  );
};

const useStyles = makeStyles((theme) => ({
  selectSearchField: {
    border: "1px solid black",
    borderRight: "none",
    borderRadius: "0.1rem",
    backgroundColor: "#F0F0F0",
    minWidth: "190px",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  searchTextField: {
    flex: 1,
    minWidth: "260px",
    border: "1px solid black",
    borderTopRightRadius: "8px",
    borderBottomRightRadius: "8px",
    borderTopLeftRadius: "0px",
    borderBottomLeftRadius: "0px",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  // BCM-881 — the orange search action closing the Search by control.
  searchButton: {
    backgroundColor: "#EB5E28",
    color: "#fff",
    height: "40px",
    width: "48px",
    borderRadius: "0 7px 7px 0",
    "&:hover": { backgroundColor: "#d4552a" },
    "&.Mui-disabled": { backgroundColor: "#EB5E28", color: "#fff", opacity: 0.6 },
  },
  // BCM-881 — the bold labels over Search by and the four filters.
  filterLabel: {
    fontWeight: "bold",
    marginBottom: "6px",
  },
  outlineButton: {
    borderRadius: "2rem",
    whiteSpace: "nowrap",
    cursor: "pointer",
    borderColor: "black",
    textTransform: "none",
    padding: "8px 12px",
    fontSize: "0.875rem",
    "&:hover": {
      backgroundColor: "#000000",
      color: "#fff",
      textTransform: "none",
    },
  },
  downloadButton: {
    borderRadius: "2rem",
    whiteSpace: "nowrap",
    cursor: "pointer",
    backgroundColor: "#ec6535",
    color: "#fff",
    textTransform: "none",
    padding: "8px 12px",
    fontSize: "0.875rem",
    "&:hover": {
      backgroundColor: "#000000",
      color: "#fff",
      textTransform: "none",
    },
  },
  clearText: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    textDecoration: "underline",
    "&:hover": {
      color: "#173072",
      textDecoration: "underline",
    },
  },
  // BCM-881 — the three reconciliation summary tiles.
  summaryTile: {
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    padding: "8px 14px",
    minWidth: "160px",
    backgroundColor: "#fff",
  },
  summaryLabel: {
    fontSize: "0.7rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#9e9e9e",
    lineHeight: 1.3,
  },
  summaryValue: {
    fontSize: "1.5rem",
    fontWeight: 500,
    lineHeight: 1.2,
  },
  summaryDelta: {
    fontSize: "0.75rem",
    whiteSpace: "nowrap",
  },
  // BCM-881 — the blue "Download excel" button the FSD moves to the summary
  // row, distinguished from the two rounded outline actions beside it.
  downloadButtonBlue: {
    borderRadius: "2rem",
    whiteSpace: "nowrap",
    cursor: "pointer",
    backgroundColor: "#286acf",
    color: "#fff",
    textTransform: "none",
    padding: "8px 16px",
    fontSize: "0.875rem",
    "&:hover": {
      backgroundColor: "#1d50a0",
      color: "#fff",
    },
  },
  // BCM-881 — the table header stays on scrolling down but scrolls across with
  // the columns: MUI's stickyHeader sets left: 0 on every header cell, which
  // pins them all on a horizontal scroll.
  headerRow: {
    "& > th": { left: "auto" },
  },
  // BCM-881 — the "+1" / "+2" pill in the CMO Batch Number column.
  moreBatchesButton: {
    minWidth: "auto",
    padding: "0 10px",
    height: "22px",
    borderRadius: "11px",
    fontSize: "0.75rem",
    textTransform: "none",
    marginLeft: "8px",
    backgroundColor: "#e8eef9",
    color: "#286acf",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#d7e2f5", boxShadow: "none" },
  },
  // The "+N" pill while its CMO batch rows are open.
  moreBatchesButtonOpen: {
    backgroundColor: "#286acf",
    color: "#fff",
    "&:hover": { backgroundColor: "#1d50a0", boxShadow: "none" },
  },
}));

function ReconciliationOfInventoryDialog(props) {
  const classes = useStyles();
  const { onBack, onClose, cmoName, apiPayload, selectedCMO, sponsorLastUpdatedDate, cmoOptions = [] } = props;
  const [cmoLastUpdatedDate, setCMOLastUpdatedDate] = useState(null);

  /* ---------------------------------------------------------------------
     BCM-881 — Search by, top filters, summary, custom report
     --------------------------------------------------------------------- */

  /*
    Search by. The criterion and the value being typed or picked, and the pair
    actually applied — the FSD runs the search on the search action or on
    Enter, not on every keystroke.
  */
  const [searchBy, setSearchBy] = useState("all");
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState({ criterion: null, value: "" });

  /*
    The CMO filter is a single selection and defaults to the CMO chosen in the
    Reconcile Inventory popup. Choosing another reloads the reconciliation for
    it; every request on this page goes out under `activePayload`.
  */
  const [activeCMO, setActiveCMO] = useState(
    selectedCMO?.organizationId
      ? { organizationId: selectedCMO.organizationId, organizationName: selectedCMO.organizationName || cmoName }
      : null
  );
  const activePayload = useMemo(
    () =>
      apiPayload && activeCMO?.organizationId
        ? { ...apiPayload, vendorMosymphonyID: activeCMO.organizationId }
        : apiPayload,
    [apiPayload, activeCMO?.organizationId]
  );
  const activeCmoName = activeCMO?.organizationName || cmoName || "";

  /*
    Plant ID, Brand and Material Type are multi selection, populated for the
    CMO in view, and go to the API as vendorPlant, brand and materialTypeID.
  */
  const [filterPlants, setFilterPlants] = useState([]);
  const [filterMaterialTypes, setFilterMaterialTypes] = useState([]);
  const [filterBrands, setFilterBrands] = useState([]);
  /*
    BCM-881 — as on the Purchase Order dashboard: every receiving plant with
    the CMOs it serves ({ plantCode, allVendorMosymphonyIDs }).
  */
  const [allReceivingPlants, setAllReceivingPlants] = useState([]);
  const [materialTypeOptions, setMaterialTypeOptions] = useState([]);
  const [customReportOpen, setCustomReportOpen] = useState(false);

  /*
    BCM-881 — the reconciliation run configured for this CMO, read only here.
    fetchReconciliationJobRunConfigurations returns one row per CMO; this page
    shows the one for the CMO in view.
  */
  const [reconciliationRunSchedule, setReconciliationRunSchedule] = useState("");

  // Which CMO batch rows are open under a BMS batch, for the "+N" control.
  const [openCmoBatches, setOpenCmoBatches] = useState({});

  const fetchReconciliationData = async (customPayload = null) => {
    try {
      const payload = customPayload || activePayload;
      const response = await axios.post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchInventoryReconciliationData`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      setCMOLastUpdatedDate(response.data?.cmoLatestInventorySyncTime)
      return {
        data: response.data?.inventoryReconciliationData || [],
        total: response.data?.inventoryReconciliationData?.length || 0,
        summary: response.data?.reconciliationSummary || null
      };
    } catch (error) {
      console.error("Error fetching reconciliation data:", error);
      return {
        data: [],
        total: 0,
        summary: null
      };
    }
  };

  const [reconciliationData, setReconciliationData] = useState([]);
  // The CMO's whole reconciliation, unfiltered — the Brand and Material Type
  // values are taken from it.
  const [summaryRows, setSummaryRows] = useState([]);
  // The API's reconciliationSummary (current, previous and change per figure), for the tiles.
  const [reconciliationSummary, setReconciliationSummary] = useState(null);

  const [editingRowId, setEditingRowId] = useState(null);
  const [editedReasons, setEditedReasons] = useState({});
  const [displayCount, setDisplayCount] = useState(25);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [collabrixReconciliationID, setCollabrixReconciliationID] = useState(0);
  const [savingIndex, setSavingIndex] = useState(null);

  const handleBack = () => {
    if (onClose) {
      onClose();
    }
    if (onBack) {
      onBack();
    }
  };

  /*
    BCM-881 — the key of a row on screen, for its edit, save and "+N" state.
    collabrixBatchReconciliationID alone is not unique in the response (the
    same ID can come back on two rows), so the row's position is part of it.
    The position is stable: the API filters the rows and a refetch replaces
    them all. Saving still sends the row's own collabrixBatchReconciliationID.
  */
  const rowKeyOf = (row, index) => `${row?.collabrixBatchReconciliationID ?? "row"}-${index}`;

  /*
    BCM-881 — the fetchInventoryReconciliationData request for the CMO in view,
    with the filters and the search applied. Defaults are the ones the
    Reconcile Inventory popup sends; the stored run ID keeps every request on
    the run first loaded.
  */
  const requestFor = ({
    plants = filterPlants,
    materialTypes = filterMaterialTypes,
    brands = filterBrands,
    search = appliedSearch,
    runId = collabrixReconciliationID,
  } = {}) => {
    const request = {
      ...activePayload,
      vendorPlant: plants.length ? plants : ["all"],
      materialTypeID: materialTypes.length ? materialTypes : ["all"],
      brand: brands.length ? brands : ["all"],
    };
    if (runId !== 0) {
      request.collabrixReconciliationID = runId;
    }
    const criterion = SEARCH_CRITERIA.find((c) => c.value === search.criterion);
    if (criterion && search.value !== "") {
      Object.assign(request, criterion.toRequest(search.value));
    }
    return request;
  };

  // The receiving plants that serve a CMO.
  const plantsOfCmo = (cmoId) =>
    allReceivingPlants.filter((plant) => plant.allVendorMosymphonyIDs.includes(cmoId));

  /*
    The Plant ID, Brand and Material Type selections the rows were last loaded
    with. As on the Purchase Order dashboard, ticking options only changes the
    selection; the rows are reloaded once, when the dropdown closes, and only
    if the selection differs from these.
  */
  const appliedFilters = useRef({ plants: [], materialTypes: [], brands: [] });

  const isFiltered = (filters = {}) => {
    const plants = filters.plants ?? filterPlants;
    const materialTypes = filters.materialTypes ?? filterMaterialTypes;
    const brands = filters.brands ?? filterBrands;
    const search = filters.search ?? appliedSearch;
    return plants.length > 0 || materialTypes.length > 0 || brands.length > 0 || !!search.criterion;
  };

  /*
    Loads the rows for the filters given. The summary covers the whole run, so
    it is taken only from an unfiltered response: a filter or a search changes
    the rows, not the run, and keeps the summary already shown. Only a change
    of reasons (`refreshSummary`) moves it, and then, with filters on, the
    whole run is fetched once more for it.
    `spinner: false` refreshes in place, as saving one reason always has.
  */
  const loadRows = async (filters = {}, { spinner = true, refreshSummary = false } = {}) => {
    appliedFilters.current = {
      plants: filters.plants ?? filterPlants,
      materialTypes: filters.materialTypes ?? filterMaterialTypes,
      brands: filters.brands ?? filterBrands,
    };
    if (spinner) setIsInitialLoading(true);
    try {
      const result = await fetchReconciliationData(requestFor(filters));
      setReconciliationData(result.data);
      setDisplayCount(25);
      setOpenCmoBatches({});
      if (!isFiltered(filters)) {
        setSummaryRows(result.data);
        setReconciliationSummary(result.summary);
      } else if (refreshSummary) {
        const whole = await fetchReconciliationData(
          requestFor({ plants: [], materialTypes: [], brands: [], search: NO_SEARCH })
        );
        setSummaryRows(whole.data);
        setReconciliationSummary(whole.summary);
      }
    } catch (error) {
      console.error("Error fetching reconciliation data:", error);
    } finally {
      if (spinner) setIsInitialLoading(false);
    }
  };

  const handleEditReason = (row, index) => {
    setEditingRowId(index);
    setEditedReasons((prev) => ({ ...prev, [index]: row.reasonForDifference }));
  };

  const handleSaveReason = async (row, index) => {
    setSavingIndex(index);
    try {
      const payload = {
        collabrixBatchReconciliationID: row.collabrixBatchReconciliationID,
        reasonForDifference: editedReasons[index] ?? row.reasonForDifference,
        vendorMosymphonyID: activePayload?.vendorMosymphonyID,
        brandOwnerMosymphonyID: activePayload?.brandOwnerMosymphonyID,
        clearReasonForDifference: false,
        collabrixReconciliationID: collabrixReconciliationID,
      };

      const response = await axios.post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateInventoryReconciliationData`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      if (response.data?.status) {
        // Refresh data from API on success, as currently filtered
        await loadRows({}, { spinner: false, refreshSummary: true });
        setEditingRowId(null);
      } else {
        console.error("Failed to update reason:", response.data?.message);
        alert("Failed to update reason. Please try again.");
      }
    } catch (error) {
      console.error("Error updating reason:", error);
    } finally {
      setSavingIndex(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
  };

  const handleClearReasons = async () => {
    setIsInitialLoading(true);
    try {
      const payload = {
        brandOwnerMosymphonyID: activePayload?.brandOwnerMosymphonyID,
        clearReasonForDifference: true,
        vendorMosymphonyID: activePayload?.vendorMosymphonyID,
        collabrixReconciliationID: collabrixReconciliationID,
        reasonForDifference: ""
      };

      const response = await axios.post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateInventoryReconciliationData`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      if (response.data?.status) {
        // Refresh data from API on success, as currently filtered
        await loadRows({}, { refreshSummary: true });
      } else {
        console.error("Failed to clear reasons:", response.data?.message);
        alert("Failed to clear reason for difference. Please try again.");
      }
    } catch (error) {
      console.error("Error clearing reasons:", error);
      alert("An error occurred while clearing. Please try again.");
    } finally {
      setIsInitialLoading(false);
    }
  };

  /*
    BCM-881 — Clear All Filters resets Plant ID, Brand and Material Type to All
    and clears the search, as the FSD states, and reloads the run unfiltered.
  */
  const handleClearFilters = () => {
    setSearchBy("all");
    setSearchValue("");
    setAppliedSearch(NO_SEARCH);
    setFilterPlants([]);
    setFilterMaterialTypes([]);
    setFilterBrands([]);
    loadRows({ plants: [], materialTypes: [], brands: [], search: NO_SEARCH });
  };

  // The search runs on the search action or on Enter (FSD), on top of the
  // filters already applied.
  const applySearch = () => {
    const value = typeof searchValue === "string" ? searchValue.trim() : searchValue;
    const search = searchBy === "all" || value === "" ? NO_SEARCH : { criterion: searchBy, value };
    setAppliedSearch(search);
    loadRows({ search });
  };

  const clearSearch = () => {
    setSearchValue("");
    setAppliedSearch(NO_SEARCH);
    loadRows({ search: NO_SEARCH });
  };

  // A filter applies as soon as it is changed.
  // A filter's selection changes as options are ticked; nothing is fetched yet.
  const changePlants = (plants) => setFilterPlants(plants);
  const changeMaterialTypes = (materialTypes) => setFilterMaterialTypes(materialTypes);
  const changeBrands = (brands) => setFilterBrands(brands);

  // On closing a filter dropdown: reload once, if any selection has changed.
  const applyFiltersOnClose = () => {
    const sameAs = (a, b) => a.length === b.length && a.every((v) => b.includes(v));
    const applied = appliedFilters.current;
    if (
      sameAs(filterPlants, applied.plants) &&
      sameAs(filterMaterialTypes, applied.materialTypes) &&
      sameAs(filterBrands, applied.brands)
    ) {
      return;
    }
    loadRows({ plants: filterPlants, materialTypes: filterMaterialTypes, brands: filterBrands });
  };

  /*
    Build the human sentence the FSD shows, e.g.
    "Every Monday, 02:00 UTC" or "January, April - day 1, 02:00 UTC".
  */
  const describeRun = (config) => {
    if (!config) return "";
    const when =
      config.triggerPoint === "Month Wise"
        ? [config.monthName, config.dayOfTheMonth ? `day ${config.dayOfTheMonth}` : ""]
          .filter(Boolean)
          .join(" - ")
        : config.dayName
          ? `Every ${config.dayName}`
          : "";
    // The time zone follows the time it qualifies: "02:00 UTC", not "02:00, UTC".
    const at = [config.time, config.timeZone].filter(Boolean).join(" ");
    return [when, at].filter(Boolean).join(", ");
  };

  useEffect(() => {
    let cancelled = false;
    const loadRunConfig = async () => {
      try {
        const res = await axios.post(
          `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchReconciliationJobRunConfigurations`,
          { sponsorId: apiPayload?.brandOwnerMosymphonyID }
        );
        const list =
          res?.data?.reconciliationJobRunConfigurations ||
          res?.data?.data?.reconciliationJobRunConfigurations ||
          [];
        const mine = list.find(
          (row) => String(row?.vendorMosymphonyID) === String(activeCMO?.organizationId)
        );
        if (!cancelled) setReconciliationRunSchedule(describeRun(mine));
      } catch (err) {
        // Not delivered yet — the line simply does not appear.
        console.error("fetchReconciliationJobRunConfigurations:", err);
      }
    };
    loadRunConfig();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCMO?.organizationId]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (scrollHeight - scrollTop - clientHeight < 100 && displayCount < visibleRows.length) {
      loadMoreRows();
    }
  };

  const loadMoreRows = () => {
    setDisplayCount(prev => Math.min(prev + 25, visibleRows.length));
  };

  /*
    Loads the latest reconciliation for the CMO in view — on opening, and again
    whenever another CMO is chosen in the CMO filter.

    As on the Purchase Order dashboard, a change of CMO keeps the filters: the
    plants selected stay where the new CMO is served by them and are dropped
    where it is not; Brand, Material Type and the search are kept. The previous
    CMO's run ID and open rows do not carry over — the latest run of the new
    CMO is loaded.
  */
  useEffect(() => {
    const loadInitialData = async () => {
      setIsInitialLoading(true);
      setCollabrixReconciliationID(0);
      setOpenCmoBatches({});
      setEditingRowId(null);
      setDisplayCount(25);

      const plants = filterPlants.filter((plantCode) =>
        plantsOfCmo(activeCMO?.organizationId).some((plant) => plant.plantCode === plantCode)
      );
      setFilterPlants(plants);
      const filters = { plants, runId: 0 };
      appliedFilters.current = { plants, materialTypes: filterMaterialTypes, brands: filterBrands };

      try {
        const result = await fetchReconciliationData(requestFor(filters));
        setReconciliationData(result.data);
        // The summary and the run ID come from the whole run.
        const whole = isFiltered(filters)
          ? await fetchReconciliationData(
            requestFor({ plants: [], materialTypes: [], brands: [], search: NO_SEARCH, runId: 0 })
          )
          : result;
        setSummaryRows(whole.data);
        setReconciliationSummary(whole.summary);
        // Store collabrixReconciliationID from first record
        if (whole.data && whole.data.length > 0 && whole.data[0].collabrixReconciliationID) {
          setCollabrixReconciliationID(whole.data[0].collabrixReconciliationID);
        }
      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setIsInitialLoading(false);
      }
    };

    if (activePayload) {
      loadInitialData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePayload]);

  /*
    BCM-881 — the receiving plants, fetched once, as the Purchase Order
    dashboard fetches them: grouped by plant code with every CMO that holds
    them.
  */
  useEffect(() => {
    let cancelled = false;
    const base = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}`;

    axios
      .get(`${base}/fetchAllReceivingPlantIDsAndStorageLocationIDsDirect/all`)
      .then((res) => {
        if (cancelled) return;
        const groups = {};
        (res.data?.vendorOrgLevelData || []).forEach((item) => {
          (item.receivingPlantIDs || []).filter(Boolean).forEach((plantCode) => {
            if (!groups[plantCode]) groups[plantCode] = { plantCode, allVendorMosymphonyIDs: [] };
            if (!groups[plantCode].allVendorMosymphonyIDs.includes(item.vendorMosymphonyID)) {
              groups[plantCode].allVendorMosymphonyIDs.push(item.vendorMosymphonyID);
            }
          });
        });
        setAllReceivingPlants(Object.values(groups));
      })
      .catch((err) => {
        console.error("Error fetching plant data:", err);
        if (!cancelled) setAllReceivingPlants([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /*
    BCM-881 — the Material Type values. The endpoint is the one the Search by
    dropdown read before BCM-881.
  */
  useEffect(() => {
    let cancelled = false;
    const base = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}`;

    axios
      .get(`${base}/fetchMaterialTypes/true`)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.status && res.data?.materialTypeList) {
          setMaterialTypeOptions(
            res.data.materialTypeList.filter((type) => type.activated === true)
          );
        }
      })
      .catch((err) => {
        console.error("Error fetching material types:", err);
      });

    return () => {
      cancelled = true;
    };
  }, [activeCMO?.organizationId]);

  const handleDownloadExcel = async () => {
    try {
      // The reconciliation as shown: the same filters and search as the table.
      const downloadPayload = requestFor();

      const response = await axios.post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/inventoryReconciliationDownloadExcel`,
        downloadPayload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });


      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Inventory_Reconciliation_${activeCmoName}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error("Error downloading excel:", error);
      alert("Failed to download excel file. Please try again.");
    }
  };

  /* ---------------------------------------------------------------------
     BCM-881 — filter options, row filtering and the summary
     --------------------------------------------------------------------- */

  /*
    The batches shown. The API applies the filters and the search; the FSD
    also excludes a batch where both BMS total qty. and CMO total qty. are zero
    — one carrying a quantity on either side stays, as that is a difference to
    be explained.
  */
  const visibleRows = (reconciliationData || []).filter((row) => !isZeroZeroRow(row));

  /*
    Brand and Material Type values, populated from the CMO's reconciliation
    (FSD: "populated based on the CMO selected"). Brand is the brands on its
    rows. Material Type goes to the API as materialTypeID, so the values are
    the material types whose name appears on its rows; any already selected
    stay listed.
  */
  const brandOptions = [...new Set((summaryRows || []).map((row) => row.brand).filter(Boolean))].sort();
  const rowMaterialTypes = new Set((summaryRows || []).map((row) => row.materialType).filter(Boolean));
  const materialTypeChoices = materialTypeOptions.filter(
    (type) =>
      rowMaterialTypes.has(type.materialTypeName) || filterMaterialTypes.includes(type.materialTypeID)
  );

  /*
    BCM-881 — the reconciliation summary comes from the API's
    reconciliationSummary (current, previous and change per figure), taken from
    the unfiltered response so it covers the reconciliation as a whole rather
    than the rows the filters leave.
  */
  const summary = reconciliationSummary || {};

  /*
    The FSD colours the change by whether the movement is an improvement, not
    by whether it went up or down: more batches reconciled is good, more rows
    with a difference is not.
  */
  const renderDelta = (metric, higherIsBetter) => {
    if (metric?.previous === null || metric?.previous === undefined) return null;
    const previous = Number(metric.previous);
    const change =
      metric.change !== null && metric.change !== undefined
        ? Number(metric.change)
        : Number(metric.current ?? 0) - previous;
    const colour =
      change === 0 ? "#9e9e9e" : (change > 0) === higherIsBetter ? "#2e7d32" : "#c62828";
    return (
      <Typography className={classes.summaryDelta} style={{ color: colour }}>
        {change === 0 ? "no change" : `${change > 0 ? "+" : "-"}${Math.abs(change)}`}
        <span style={{ color: "#9e9e9e" }}>{`  vs last reconciliation ${previous}`}</span>
      </Typography>
    );
  };

  const summaryTile = (label, metric, higherIsBetter) => (
    <Box className={classes.summaryTile}>
      <Typography className={classes.summaryLabel}>{label}</Typography>
      <Typography className={classes.summaryValue}>{metric?.current ?? 0}</Typography>
      {renderDelta(metric, higherIsBetter)}
    </Box>
  );

  const activeCriterion = SEARCH_CRITERIA.find((c) => c.value === searchBy);

  /*
    The CMOs offered in the CMO filter: those the Reconcile Inventory popup
    offered, and always the one in view. As on the Purchase Order dashboard,
    with plants selected only the CMOs those plants serve are offered.
  */
  const cmoChoices = (() => {
    let list = (cmoOptions || []).filter((c) => c?.organizationId && c.organizationId !== "All");
    if (filterPlants.length) {
      const servedBy = filterPlants.flatMap(
        (plantCode) =>
          allReceivingPlants.find((plant) => plant.plantCode === plantCode)?.allVendorMosymphonyIDs || []
      );
      list = list.filter((c) => servedBy.includes(c.organizationId));
    }
    if (activeCMO?.organizationId && !list.some((c) => c.organizationId === activeCMO.organizationId)) {
      return [activeCMO, ...list];
    }
    return list;
  })();

  /*
    The Plant ID values, populated from the CMO's reconciliation like Brand and
    Material Type: the plants on its rows. Any already selected stay listed.
  */
  const plantOptions = [
    ...new Set([...(summaryRows || []).map((row) => row.vendorPlant).filter(Boolean), ...filterPlants]),
  ].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));

  return (
    <Dialog open={true} fullScreen>
      <DialogContent style={{ padding: "0", height: "90vh", display: "flex", flexDirection: "column" }}>
        <Box style={{ padding: "20px", paddingTop: 0, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Header */}
          <Box
            style={{ marginTop: "1em", display: "flex", gap: "1em" }}
          // onClick={handleBack}
          >
            <Typography
              style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
              onClick={handleBack}
            >
              <ArrowBackIosIcon />
              Back
            </Typography>
            <Typography variant='h5' style={{ fontWeight: 600, display: "flex", alignItems: "center" }}>
              Reconciliation of Inventory
              {/* BCM-881: the UoM conversion note, which the FSD moves off the
                  page and onto this ⓘ. */}
              <InfoTip title={RECON_TOOLTIP.title} />
            </Typography>
          </Box>
          <Divider style={{ margin: "1em 0" }} />

          {/*
            BCM-881 — Search by, followed on the same row by the four filters
            and Clear All Filters.
          */}
          <Box style={{ display: "flex", alignItems: "flex-end", gap: "16px", flexWrap: "wrap" }}>
            {/* Search Section */}
            <Box style={{ display: "flex", flexDirection: "column", alignItems: "start" }}>
              <Typography variant="subtitle1" className={classes.filterLabel}>
                Search by:
              </Typography>
              <Box style={{ display: "flex", alignItems: "center" }}>
                {/*
                  BCM-881 — the five criteria. Material Type and Plant ID are
                  filters of their own on this row now.
                */}
                <TextField
                  size="small"
                  select
                  value={searchBy}
                  onChange={(event) => {
                    setSearchBy(event.target.value);
                    setSearchValue("");
                  }}
                  variant="outlined"
                  className={classes.selectSearchField}
                >
                  <MenuItem value="all">Select Criteria</MenuItem>
                  {SEARCH_CRITERIA.map((criterion) => (
                    <MenuItem key={criterion.value} value={criterion.value}>
                      {criterion.value}
                    </MenuItem>
                  ))}
                </TextField>

                {/*
                  Batch Allocation Done and Difference replace the free text
                  box with a dropdown of their own values.
                */}
                {activeCriterion?.kind === "choice" ? (
                  <FormControl
                    variant="outlined"
                    size="small"
                    className={classes.searchTextField}
                  >
                    <Select
                      displayEmpty
                      value={searchValue}
                      onChange={(event) => setSearchValue(event.target.value)}
                    >
                      <MenuItem value="">
                        <span style={{ color: "#9e9e9e" }}>Select</span>
                      </MenuItem>
                      {activeCriterion.options.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    size="small"
                    value={searchValue}
                    disabled={searchBy === "all"}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") applySearch();
                    }}
                    variant="outlined"
                    placeholder="Search"
                    className={classes.searchTextField}
                    InputProps={{
                      endAdornment: searchValue ? (
                        <InputAdornment position="end">
                          <Tooltip title="Clear search" aria-label="clear">
                            <Clear
                              fontSize="small"
                              onClick={clearSearch}
                              style={{ cursor: "pointer", color: "dimgray" }}
                            />
                          </Tooltip>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                )}
                <IconButton
                  size="small"
                  className={classes.searchButton}
                  disabled={searchBy === "all"}
                  onClick={applySearch}
                  aria-label="search"
                >
                  <Search />
                </IconButton>
              </Box>
            </Box>

            {/*
              BCM-881 — CMO is a single selection, defaulting to the CMO chosen
              in the Reconcile Inventory popup; choosing another reloads the
              page for it. Plant ID, Brand and Material Type are multi
              selection, apply together, and go to the API as vendorPlant,
              brand and materialTypeID. CMO and Plant ID narrow each other as
              on the Purchase Order dashboard; Brand stands on its own.
            */}
            <Box>
              <Typography variant="subtitle1" className={classes.filterLabel}>CMO:</Typography>
              <FormControl variant="outlined" size="small" style={{ minWidth: 240, display: "block" }}>
                <Select
                  fullWidth
                  value={activeCMO?.organizationId || ""}
                  onChange={(event) => {
                    const next = cmoChoices.find((c) => c.organizationId === event.target.value);
                    if (next) setActiveCMO(next);
                  }}
                >
                  {cmoChoices.map((cmo) => (
                    <MenuItem key={cmo.organizationId} value={cmo.organizationId}>
                      {cmo.organizationName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {[
              {
                label: "Plant ID:",
                options: plantOptions.map((plant) => ({ value: plant, label: String(plant) })),
                value: filterPlants,
                set: changePlants,
              },
              {
                label: "Brand:",
                options: brandOptions.map((brand) => ({ value: brand, label: brand })),
                value: filterBrands,
                set: changeBrands,
              },
              {
                label: "Material Type:",
                options: materialTypeChoices.map((type) => ({
                  value: type.materialTypeID,
                  label: type.materialTypeName,
                })),
                value: filterMaterialTypes,
                set: changeMaterialTypes,
              },
            ].map((filter) => (
              <Box key={filter.label}>
                <Typography variant="subtitle1" className={classes.filterLabel}>{filter.label}</Typography>
                <FormControl variant="outlined" size="small" style={{ minWidth: 160, display: "block" }}>
                  <Select
                    multiple
                    fullWidth
                    displayEmpty
                    value={filter.value}
                    onChange={(event) => filter.set(event.target.value)}
                    onClose={applyFiltersOnClose}
                    renderValue={(selected) =>
                      selected.length === 0
                        ? "All"
                        : selected.length === 1
                          ? filter.options.find((o) => o.value === selected[0])?.label ?? String(selected[0])
                          : `${selected.length} selected`
                    }
                  >
                    {filter.options.length === 0 && (
                      <MenuItem disabled value="">
                        <span style={{ color: "#9e9e9e" }}>No values</span>
                      </MenuItem>
                    )}
                    {filter.options.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Checkbox size="small" checked={filter.value.indexOf(option.value) > -1} />
                        <ListItemText primary={option.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            ))}

            <Typography
              variant="caption"
              style={{ fontSize: 14, color: "#286ACF", marginBottom: "10px" }}
              className={classes.clearText}
              onClick={handleClearFilters}
            >
              Clear All Filters
            </Typography>
          </Box>

          {/*
            BCM-881 — the summary row: the three reconciliation tiles at the
            left, the BMS and CMO inventory timestamps and the configured
            reconciliation run in the middle, and the three page actions at the
            right.

            This replaces the old row, which carried the UoM note (now the ⓘ on
            the page title) and the timestamps alone. The View Reconciliation
            History button is removed, "Clear Reason for Difference Column
            Values" is renamed Clear Reasons, Custom Report is added, and
            Download excel becomes the blue button beside them.
          */}
          <Box style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", margin: "20px 0", flexWrap: "wrap" }}>
            <Box style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              {summaryTile("Batches reconciled", summary.batchesReconciled, true)}
              {summaryTile("Rows with a difference", summary.rowsWithDifference, false)}
              {summaryTile("Differences without a reason", summary.differencesWithoutReason, false)}
            </Box>

            <Box style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {sponsorLastUpdatedDate && <Typography variant="body2" style={{ fontSize: "14px" }}>
                BMS inventory - {convertToLocaleDateTime(sponsorLastUpdatedDate)}
              </Typography>}
              {cmoLastUpdatedDate && <Typography variant="body2" style={{ fontSize: "14px" }}>
                CMO inventory - {convertToLocaleDateTime(cmoLastUpdatedDate)}
              </Typography>}
              {/* Read only here — the schedule is set per CMO in Sponsor
                  Network Configuration, and reaches this page from the API. */}
              {reconciliationRunSchedule && (
                <Typography variant="body2" style={{ fontSize: "14px" }}>
                  Reconciliation run -  {reconciliationRunSchedule}
                </Typography>
              )}
            </Box>

            <Box style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <Button
                variant="outlined"
                className={classes.outlineButton}
                onClick={handleClearReasons}
              >
                Clear Reasons
              </Button>
              <Button
                variant="outlined"
                className={classes.outlineButton}
                onClick={() => setCustomReportOpen(true)}
              >
                Custom Report
              </Button>
              <Button
                variant="contained"
                startIcon={<GetApp />}
                className={classes.downloadButtonBlue}
                onClick={handleDownloadExcel}
              >
                Download excel
              </Button>
            </Box>
          </Box>

          {/* Reconciliation Table */}
          <TableContainer
            component={Paper}
            style={{ flex: 1, overflow: "auto" }}
            onScroll={handleScroll}
          >
            <Table stickyHeader>
              <TableHead>
                {/*
                  BCM-881 — the column order the FSD fixes. CMO ID, Brand and
                  Material Type are added at the start beside the Plant ID that
                  was already here; BMS Expiry Date, CMO Expiry Date and
                  Allocated Qty. are added; Material Code / Description gains
                  its BMS prefix and the two on-hand quantities become the two
                  totals.
                */}
                <TableRow style={{ backgroundColor: "#f5f5f5" }} className={classes.headerRow}>
                  <TableCell style={{ minWidth: COL_MIN_WIDTH.cmoId }}>CMO ID</TableCell>
                  <TableCell style={{ minWidth: COL_MIN_WIDTH.plantId }}>Plant ID</TableCell>
                  <TableCell style={{ minWidth: COL_MIN_WIDTH.brand }}>Brand</TableCell>
                  <TableCell style={{ minWidth: COL_MIN_WIDTH.materialType }}>Material Type</TableCell>
                  <TableCell style={{ minWidth: COL_MIN_WIDTH.materialCode }}>BMS Material Code / Description</TableCell>
                  <TableCell style={{ minWidth: COL_MIN_WIDTH.uom }}>BMS UoM</TableCell>
                  <TableCell style={{ minWidth: COL_MIN_WIDTH.bmsBatch }}>BMS Batch Number</TableCell>
                  <TableCell style={{ minWidth: COL_MIN_WIDTH.cmoBatch }}>CMO Batch Number</TableCell>
                  <TableCell style={{ minWidth: COL_MIN_WIDTH.bmsExpiry }}>BMS Expiry Date</TableCell>
                  <TableCell style={{ minWidth: COL_MIN_WIDTH.cmoExpiry }}>CMO Expiry Date</TableCell>
                  <TableCell align="right" style={{ minWidth: COL_MIN_WIDTH.bmsTotal }}>
                    BMS Total Qty.<InfoTip title={RECON_TOOLTIP.bmsTotalQty} />
                  </TableCell>
                  <TableCell align="right" style={{ minWidth: COL_MIN_WIDTH.cmoTotal }}>
                    CMO Total Qty.
                    <InfoTip title={RECON_TOOLTIP.cmoTotalQty} />
                  </TableCell>
                  <TableCell align="right" style={{ minWidth: COL_MIN_WIDTH.allocated }}>
                    Allocated Qty.<InfoTip title={RECON_TOOLTIP.allocatedQty} />
                  </TableCell>
                  <TableCell align="right" style={{ minWidth: COL_MIN_WIDTH.difference }}>
                    Difference Between BMS Total Qty. And CMO Total Qty.
                  </TableCell>
                  <TableCell style={{ minWidth: "300px", maxWidth: "300px" }}>Reason For Difference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isInitialLoading ? (
                  <TableRow>
                    <TableCell colSpan={15} align="center" style={{ padding: "40px" }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : visibleRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={15} align="center" style={{ padding: "40px" }}>
                      <Typography variant="body2" color="textSecondary">
                        No records found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {visibleRows.slice(0, displayCount).map((row, index) => (
                      <React.Fragment key={rowKeyOf(row, index)}>
                      <TableRow>
                        <TableCell>{cmoIdCell(row)}</TableCell>
                        <TableCell>{row.vendorPlant}</TableCell>
                        <TableCell>{row.brand || ""}</TableCell>
                        <TableCell>{row.materialType || ""}</TableCell>
                        <TableCell>
                          {/* BCM-881: where no BMS batch matched, the material
                              details the CMO reported are shown instead, each
                              marked "(CMO)" by the API. */}
                          {materialCodeCell(row)}
                        </TableCell>
                        <TableCell>{uomCell(row)}</TableCell>
                        <TableCell>{row.sponsorBatchNumber}</TableCell>
                        <TableCell>
                          {row.cmoBatchNumber}
                          {/* BCM-881: "+N" opens the further CMO batches
                              mapped to this BMS batch. */}
                          {extraCmoBatchesOf(row).length > 0 && (
                            <Button
                              size="small"
                              variant="contained"
                              disableElevation
                              className={`${classes.moreBatchesButton} ${openCmoBatches[rowKeyOf(row, index)] ? classes.moreBatchesButtonOpen : ""}`}
                              onClick={() =>
                                setOpenCmoBatches((prev) => ({ ...prev, [rowKeyOf(row, index)]: !prev[rowKeyOf(row, index)] }))
                              }
                            >
                              {/* The further CMO batches, and the rows it opens. */}
                              {`+${extraCmoBatchesOf(row).length}`}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>{formatReconDate(row.sponsorBatchExpiryDate)}</TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>
                          {extraCmoBatchesOf(row).length > 0 ? (
                            <span style={{ color: "#9e9e9e" }}>see batches</span>
                          ) : (
                            formatReconDate(row.cmoBatchExpiryDate)
                          )}
                        </TableCell>
                        <TableCell align="right" style={{ whiteSpace: "nowrap" }}>{formatReconQty(row.sponsorOnHandQty)}</TableCell>
                        <TableCell align="right" style={{ fontWeight: 500, whiteSpace: "nowrap" }}>
                          {/* cmoOnHandQtyDisplay, as the API formats it —
                              e.g. "2000 (KG)" where it could not be converted. */}
                          {cmoQtyCell(row)}
                        </TableCell>
                        <TableCell align="right" style={{ whiteSpace: "nowrap" }}>{formatReconQty(row.sponsorAllocatedQuantity)}</TableCell>
                        <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
                          {formatReconQty(row.difference)}
                        </TableCell>
                    <TableCell style={{ width: "300px", maxWidth: "300px", wordWrap: "break-word", whiteSpace: "normal" }}>
                      {editingRowId === rowKeyOf(row, index) ? (
                        <Box style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={6}
                            variant="outlined"
                            value={editedReasons[rowKeyOf(row, index)] ?? row.reasonForDifference}
                            onChange={(e) =>
                              setEditedReasons((prev) => ({ ...prev, [rowKeyOf(row, index)]: e.target.value }))
                            }
                            disabled={savingIndex === rowKeyOf(row, index)}
                            style={{
                              backgroundColor: "#fff",
                            }}
                          />
                          <Box style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <Tooltip title="Cancel" aria-label="cancel">
                              <IconButton
                                size="small"
                                onClick={handleCancelEdit}
                                style={{ padding: "4px" }}
                                disabled={savingIndex === rowKeyOf(row, index)}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Save" aria-label="save">
                              <IconButton 
                                size="small" 
                                onClick={() => handleSaveReason(row, rowKeyOf(row, index))}
                                style={{ padding: "4px" }}
                                disabled={savingIndex === rowKeyOf(row, index)}
                              >
                                {savingIndex === rowKeyOf(row, index) ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <SaveIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      ) : (
                        <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                          <span>{row.reasonForDifference}</span>
                          <Tooltip title="Edit reason" aria-label="edit">
                            <IconButton size="small" onClick={() => handleEditReason(row, rowKeyOf(row, index))}>
                              <Edit style={{ color: "#ff6b35" }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  {/*
                    BCM-881 — every CMO batch mapped to this BMS batch, opened by
                    "+N": the row's own batch as well as the further ones "+N"
                    counts, since the row above shows only their total. Each
                    carries its own CMO batch number, CMO total qty. and CMO
                    expiry date; the BMS values are shown once on the row above
                    and are not repeated.
                  */}
                  {openCmoBatches[rowKeyOf(row, index)] &&
                    extraCmoBatchesOf(row).length > 0 &&
                    cmoBatchesOf(row).map((cmoBatch, cmoIndex) => (
                      <TableRow key={`${rowKeyOf(row, index)}-cmo-${cmoIndex}`} style={{ backgroundColor: "#fafafa" }}>
                        <TableCell colSpan={7} />
                        <TableCell>
                          {cmoBatch?.cmoInventoryBatchNumber || cmoBatch?.cmoBatchNumber || ""}
                        </TableCell>
                        <TableCell />
                        <TableCell>
                          {formatReconDate(cmoBatch?.expiryDate || cmoBatch?.cmoBatchExpiryDate)}
                        </TableCell>
                        <TableCell />
                        <TableCell align="right" style={{ fontWeight: 500 }}>
                          {/* onHandQtyDisplay, as the API formats it. */}
                          {cmoBatch?.onHandQtyDisplay ?? ""}
                        </TableCell>
                        <TableCell colSpan={3} />
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
                {displayCount < visibleRows.length && (
                  <TableRow>
                    <TableCell colSpan={15} align="center" style={{ padding: "20px" }}>
                      <Typography variant="body2" color="textSecondary">
                        Showing {displayCount} of {visibleRows.length} records. Scroll down to load more...
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* BCM-881 — the custom report window, opened by Custom Report. */}
        {customReportOpen && (
          <CustomReportDialog
            open={customReportOpen}
            onClose={() => setCustomReportOpen(false)}
            cmoName={activeCmoName}
            brandOwnerMosymphonyID={activePayload?.brandOwnerMosymphonyID}
            vendorMosymphonyID={activePayload?.vendorMosymphonyID}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ReconciliationOfInventoryDialog;